import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { OptionFieldComponent } from "../../../shared/components/controls/option-field/option-field.component";
import { TablePagenationComponent } from "../../../shared/components/table-pagenation/table-pagenation.component";
import { NotificationHistoryPage, NotificationHistoryViewModel } from '../../../shared/models/notification-history.model';
import { NotificationHistoryService } from '../../../shared/services/notification-history.service';
import { SharedService } from '../../../shared/services/shared.service';
import { RoutesPath } from '../../../shared/constants/routes-path';
import { SrStatus } from '../../../shared/constants/status.enum';

@Component({
  selector: 'app-notifications',
  imports: [CommonModule, TranslateModule, FormsModule, ReactiveFormsModule, ButtonComponent, OptionFieldComponent, TablePagenationComponent],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss'
})
export class NotificationsComponent {
  showUnreadOnly = false;
  membershipFilterControl = new FormControl<string | null>(null);
  notifications: NotificationHistoryViewModel[] = [];
  serviceFilterOptions: Array<{ Value: string }> = [];
  loading = false;
  lang = 'en';
  currentPage = 1;
  readonly pageSize = 50;
  totalRecords = 0;

  constructor(
    private router: Router,
    private sharedService: SharedService,
    private notificationHistoryService: NotificationHistoryService
  ) {
    this.lang = this.sharedService.getCurrentLang() || 'en';
  }

  ngOnInit(): void {
    this.membershipFilterControl.valueChanges.subscribe(() => {
      this.currentPage = 1;
      this.loadNotifications();
    });
    this.loadNotifications();
  }

  loadNotifications(): void {
    const loginName = this.sharedService.getUserProfile()?.['Login Name'] ?? '';
    if (!loginName) {
      this.notifications = [];
      this.totalRecords = 0;
      return;
    }

    this.loading = true;
    const startRowNum = (this.currentPage - 1) * this.pageSize;
    const request$ = this.showUnreadOnly
      ? this.notificationHistoryService.getUnreadNotificationViewModels(
          loginName,
          this.lang,
          this.pageSize,
          startRowNum
        )
      : this.notificationHistoryService.getNotificationViewModels(
          loginName,
          this.lang,
          this.pageSize,
          startRowNum
        );

    request$.subscribe({
        next: (response: NotificationHistoryPage) => {
          this.notifications = response.notifications;
          this.totalRecords = response.totalRecords;
          this.updateServiceFilterOptions();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.notifications = [];
          this.serviceFilterOptions = [];
          this.totalRecords = 0;
          this.sharedService.toastMessage('ERROR', 'TECHNICAL_ERROR', 'error');
        }
      });
  }

  remove(id: string): void {
    this.notifications = this.notifications.filter((notification) => notification.id !== id);
    this.updateServiceFilterOptions();

    if (
      this.membershipFilterControl.value &&
      !this.serviceFilterOptions.some(
        (option) => option.Value === this.membershipFilterControl.value
      )
    ) {
      this.membershipFilterControl.setValue(null);
    }

    this.ensureValidCurrentPage();
  }

  openSettings(): void {
    this.router.navigate([`/${RoutesPath.PROFILE}`], {
      queryParams: { tab: 'notifications' }
    });
  }

  get filtered(): NotificationHistoryViewModel[] {
    const selectedMembership = this.membershipFilterControl.value;
    return this.notifications.filter((notification) =>
      (!selectedMembership || notification.membershipName === selectedMembership)
    );
  }

  get paginatedNotifications(): NotificationHistoryViewModel[] {
    return this.filtered;
  }

  get totalPages(): number {
    return Math.ceil(this.totalRecords / this.pageSize);
  }

  viewDetail(notification: NotificationHistoryViewModel): void {
    if (!notification.srNumber) {
      return;
    }

    this.markNotificationAsRead(notification);
    this.sharedService.gotoDetailPage(notification.raw);
  }

  openDownload(url: string | null): void {
    if (!url) {
      return;
    }
    window.open(url, '_blank', 'noopener');
  }

  getIconSrc(icon: NotificationHistoryViewModel['icon']): string {
    switch (icon) {
      case 'success':
        return '/icons/svg/card-success.svg';
      case 'warning':
        return '/icons/svg/warning.svg';
      case 'error':
        return '/icons/svg/error.svg';
      case 'info':
      default:
        return '/icons/svg/info.svg';
    }
  }

  shouldShowCompleteApplication(notification: NotificationHistoryViewModel): boolean {
    return notification.status?.trim().toLowerCase() === 'awaiting clarification';
  }

  shouldShowPay(notification: NotificationHistoryViewModel): boolean {
    return notification.status?.trim() === SrStatus.ApprovedAndPaymentAwaited;
  }

  goToPayment(notification: NotificationHistoryViewModel): void {
    if (!notification.srNumber) {
      return;
    }

    this.markNotificationAsRead(notification);
    this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.PAYMENT}/${notification.srNumber}`]);
  }

  private updateServiceFilterOptions(): void {
    const membershipNames = Array.from(
      new Set(
        this.notifications
          .map((notification) => notification.membershipName)
          .filter((membershipName): membershipName is string => !!membershipName)
      )
    );

    this.serviceFilterOptions = membershipNames.map((membershipName) => ({ Value: membershipName }));
  }

  private markNotificationAsRead(notification: NotificationHistoryViewModel): void {
    if (!notification.highlight || !notification.id) {
      return;
    }

    const loginName = this.sharedService.getUserProfile()?.['Login Name'] ?? '';
    if (!loginName) {
      return;
    }

    notification.highlight = false;
    this.notificationHistoryService.markAsRead(
      loginName,
      notification.id,
      notification.created
    ).subscribe({
      error: () => {
        notification.highlight = true;
      }
    });
  }

  getTimeAgo(createdOnDate: Date | null, fallback: string): string {
    if (!createdOnDate) {
      return fallback;
    }

    const diffMs = Date.now() - createdOnDate.getTime();
    if (diffMs < 0) {
      return this.formatDisplayDate(createdOnDate);
    }

    const formatter = new Intl.RelativeTimeFormat(
      this.lang === 'ar' ? 'ar' : 'en',
      { numeric: 'auto' }
    );

    const minutes = Math.floor(diffMs / (1000 * 60));
    if (minutes < 1) {
      return formatter.format(0, 'minute');
    }
    if (minutes < 60) {
      return formatter.format(-minutes, 'minute');
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return formatter.format(-hours, 'hour');
    }

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfCreatedDay = new Date(createdOnDate);
    startOfCreatedDay.setHours(0, 0, 0, 0);
    const dayDiff = Math.round(
      (startOfToday.getTime() - startOfCreatedDay.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (dayDiff <= 1) {
      return formatter.format(-dayDiff, 'day');
    }

    return this.formatDisplayDate(createdOnDate);
  }

  private formatDisplayDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  onShowUnreadOnlyChange(): void {
    this.currentPage = 1;
    this.loadNotifications();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.currentPage = page;
    this.loadNotifications();
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  prevPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  private ensureValidCurrentPage(): void {
    const totalPages = this.totalPages;
    if (totalPages < 1) {
      this.currentPage = 1;
      return;
    }
    if (this.currentPage > totalPages) {
      this.currentPage = totalPages;
    }
  }
}
